import React from 'react';
import Image from 'next/image';
import type { DetailedValue } from '../hooks/useAboutPageData';

interface DetailedValuesSectionProps {
  title: string;
  subtitle: string;
  imageSrc: string;
  imageAlt: string;
  values: DetailedValue[];
}

/**
 * ValueItem Component
 * Individual value item with title and description
 */
const ValueItem: React.FC<{ value: DetailedValue }> = ({ value }) => (
  <div>
    <h3 className="text-2xl font-bold mb-2">{value.title}</h3>
    <p className="text-green-100">
      {value.description.split('Kiến Tạo Xanh Group').map((part, index, array) => (
        <React.Fragment key={index}>
          {part}
          {index < array.length - 1 && (
            <span className="font-semibold">Kiến Tạo Xanh Group</span>
          )}
        </React.Fragment>
      ))}
    </p>
  </div>
);

/**
 * DetailedValuesSection Component
 * Displays the detailed explanation of core values with image and text
 */
const DetailedValuesSection: React.FC<DetailedValuesSectionProps> = ({
  title,
  subtitle,
  imageSrc,
  imageAlt,
  values
}) => {
  return (
    <section className="py-16 bg-green-700 text-white" aria-label="Detailed core values">
      <div className="mx-auto max-w-7xl px-8 lg:px-12">
        {/* Section Title - Left Aligned */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold tracking-tight lg:text-4xl">
            {title}
          </h2>
          <p className="mt-4 text-green-100">
            {subtitle}
          </p>
        </div>

        {/* Two Column Layout - Image Left, Content Right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          {/* Left Column - Image Only */}
          <div>
            <div className="relative rounded-xl overflow-hidden shadow-2xl aspect-[4/3]">
              <Image
                src={imageSrc}
                alt={imageAlt}
                fill
                className="object-cover rounded-xl"
                sizes="(max-width: 1024px) 100vw, 50vw"
                priority={false}
              />
            </div>
          </div>

          {/* Right Column - Core Values Explanation */}
          <div className="space-y-6">
            {values.map((value) => (
              <ValueItem key={value.id} value={value} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DetailedValuesSection;
